import {
  createContext,
  useState,
  ReactNode,
  Children,
  useMemo,
  useCallback,
} from 'react'

interface AccordionContextType {
  openIndexes: number[]
  toggleAccordion: (index: number) => void
  defaultOpenIndexes?: number[] | boolean
}

const AccordionContext = createContext<AccordionContextType | undefined>(
  undefined,
)

interface AccordionProps {
  /** The children elements that will be rendered inside the Accordion */
  children: ReactNode
  /** Option to allow multiple items to be open at once (default: false) */
  multipleOpen?: boolean
  /** The initial open accordion items or `true` to open all items
   * @example
   * ```tsx
   * // Opens the first and second items by default
   * defaultOpenIndexes={[0, 1]}
   * ```
   * @example
   * ```tsx
   * // Opens all accordion items by default
   * defaultOpenIndexes
   * ```
   */
  defaultOpenIndexes?: number[] | true
}

const Accordion = ({
  children,
  multipleOpen = false,
  defaultOpenIndexes = [],
}: AccordionProps) => {
  const totalItems = Children.count(children)

  const [openIndexes, setOpenIndexes] = useState(
    defaultOpenIndexes === true
      ? Array.from({ length: totalItems }, (_, index) => index)
      : defaultOpenIndexes,
  )

  const toggleAccordion = useCallback(
    (indexToRemove: number) => {
      if (multipleOpen) {
        setOpenIndexes((prev) =>
          prev.includes(indexToRemove)
            ? prev.filter((index) => index !== indexToRemove)
            : [...prev, indexToRemove],
        )
        return
      }
      setOpenIndexes(openIndexes[0] === indexToRemove ? [] : [indexToRemove])
    },
    [multipleOpen, openIndexes],
  )

  const contextValue = useMemo(
    () => ({
      openIndexes,
      toggleAccordion,
      defaultOpenIndexes,
    }),
    [openIndexes, toggleAccordion, defaultOpenIndexes],
  )

  return (
    <AccordionContext.Provider value={contextValue}>
      {children}
    </AccordionContext.Provider>
  )
}

export { Accordion, AccordionContext }
